import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { apiClient } from '../services/apiClient';

const tableStyles = {
  tableCell: {
    padding: '8px 16px',  // Réduit le padding vertical de 16px à 8px
    height: '50px',       // Force une hauteur fixe de 50px
    whiteSpace: 'nowrap', // Évite le retour à la ligne du texte
  },
  headerCell: {
    padding: '8px 16px',
    height: '50px',
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  }
};

export default function Roles() {
  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Roles state (Tab 1)
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({ role: '', sub_role: '' });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Translations state (Tab 2)
  const [translations, setTranslations] = useState([]);
  const [translationsLoading, setTranslationsLoading] = useState(true);
  const [openTranslationDialog, setOpenTranslationDialog] = useState(false);
  const [openDeleteTranslationDialog, setOpenDeleteTranslationDialog] = useState(false);
  const [openTranslateDialog, setOpenTranslateDialog] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [translationFormData, setTranslationFormData] = useState({ key: '', language: 'fr', label: '' });
  const [translationFormError, setTranslationFormError] = useState(null);
  const [filterKey, setFilterKey] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [translating, setTranslating] = useState(false);
  const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'it'];

  useEffect(() => {
    fetchRoles();
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      setTranslationsLoading(true);
      const params = new URLSearchParams();
      if (filterKey) params.append('key', filterKey);
      if (filterLanguage) params.append('language', filterLanguage);
      const queryString = params.toString();
      const url = `/api/admin/role-translations${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient.get(url);
      setTranslations(response);
    } catch (error) {
      console.error('Error fetching translations:', error);
      setTranslationFormError('Impossible de charger les traductions');
    } finally {
      setTranslationsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 1) {
      fetchTranslations();
    }
  }, [filterKey, filterLanguage, selectedTab]);

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get('/api/admin/roles');
      setRoles(response);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setFormError('Impossible de charger les rôles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setFormData({ role: role.role, sub_role: role.sub_role });
      setSelectedRole(role);
    } else {
      setFormData({ role: '', sub_role: '' });
      setSelectedRole(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ role: '', sub_role: '' });
    setSelectedRole(null);
  };

  const handleOpenDeleteDialog = (role) => {
    setSelectedRole(role);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRole(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      if (selectedRole) {
        const { data } = await apiClient.put(`/api/admin/roles/${selectedRole.id}`, formData);
      } else {
        const { data } = await apiClient.post('/api/admin/roles', formData);
      }

      setFormSuccess(true);
      handleCloseDialog();
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      if (error.response?.status === 409) {
        setFormError('Cette combinaison de rôle existe déjà');
      } else {
        setFormError('Impossible d\'enregistrer le rôle');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      setFormError(null);

      await apiClient.delete(`/api/admin/roles/${selectedRole.id}`);

      handleCloseDeleteDialog();
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      setFormError('Impossible de supprimer le rôle');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Translation handlers
  const handleOpenTranslationDialog = (translation = null) => {
    if (translation) {
      setTranslationFormData({ key: translation.key, language: translation.language, label: translation.label });
      setSelectedTranslation(translation);
    } else {
      setTranslationFormData({ key: '', language: 'fr', label: '' });
      setSelectedTranslation(null);
    }
    setOpenTranslationDialog(true);
  };

  const handleCloseTranslationDialog = () => {
    setOpenTranslationDialog(false);
    setTranslationFormData({ key: '', language: 'fr', label: '' });
    setSelectedTranslation(null);
  };

  const handleOpenDeleteTranslationDialog = (translation) => {
    setSelectedTranslation(translation);
    setOpenDeleteTranslationDialog(true);
  };

  const handleCloseDeleteTranslationDialog = () => {
    setOpenDeleteTranslationDialog(false);
    setSelectedTranslation(null);
  };

  const handleTranslationInputChange = (e) => {
    const { name, value } = e.target;
    setTranslationFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTranslationSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setTranslationFormError(null);

      if (selectedTranslation) {
        await apiClient.put(`/api/admin/role-translations/${selectedTranslation.id}`, translationFormData);
      } else {
        await apiClient.post('/api/admin/role-translations', translationFormData);
      }

      showSnackbar(selectedTranslation ? 'Traduction mise à jour' : 'Traduction créée', 'success');
      handleCloseTranslationDialog();
      fetchTranslations();
    } catch (error) {
      console.error('Error saving translation:', error);
      if (error.response?.status === 409) {
        setTranslationFormError('Une traduction pour cette clé et cette langue existe déjà');
      } else {
        setTranslationFormError('Impossible d\'enregistrer la traduction');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTranslation = async () => {
    try {
      setSaving(true);
      setTranslationFormError(null);

      await apiClient.delete(`/api/admin/role-translations/${selectedTranslation.id}`);

      showSnackbar('Traduction supprimée', 'success');
      handleCloseDeleteTranslationDialog();
      fetchTranslations();
    } catch (error) {
      console.error('Error deleting translation:', error);
      setTranslationFormError('Impossible de supprimer la traduction');
    } finally {
      setSaving(false);
    }
  };

  // Group translations by key
  const groupedTranslations = translations.reduce((acc, translation) => {
    if (!acc[translation.key]) {
      acc[translation.key] = [];
    }
    acc[translation.key].push(translation);
    return acc;
  }, {});

  const languages = ['fr', 'en', 'de', 'it'];
  const languageNames = { fr: 'Français', en: 'English', de: 'Deutsch', it: 'Italiano' };

  // Translation handlers
  const handleOpenTranslateDialog = (translation) => {
    setSelectedTranslation(translation);
    setOpenTranslateDialog(true);
  };

  const handleCloseTranslateDialog = () => {
    setOpenTranslateDialog(false);
    setSelectedTranslation(null);
  };

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      setTranslationFormError(null);

      const targetLanguages = SUPPORTED_LANGUAGES.filter(lang => lang !== selectedTranslation.language);
      
      await apiClient.post(`/api/admin/role-translations/${selectedTranslation.id}/translate`, {
        sourceLanguage: selectedTranslation.language,
        targetLanguages: targetLanguages
      });
      
      showSnackbar('Traduction effectuée avec succès', 'success');
      handleCloseTranslateDialog();
      fetchTranslations();
    } catch (error) {
      console.error('Error translating:', error);
      setTranslationFormError('Impossible de traduire la clé');
    } finally {
      setTranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          Gestion des rôles
        </h2>
      </div>

      <div className="px-6 py-4">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-1 rounded-xl bg-[#E0EAFE] p-1 mb-6">
            <Tab
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                  selected
                    ? 'bg-white text-gray-700 shadow'
                    : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-700'
                }`
              }
            >
              Définition des rôles
            </Tab>
            <Tab
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors ${
                  selected
                    ? 'bg-white text-gray-700 shadow'
                    : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-700'
                }`
              }
            >
              Traduction des rôles
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Tab 1: Role Definitions */}
            <Tab.Panel>
        <div className="flex justify-end mb-6">
          <button
            onClick={() => handleOpenDialog()}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="w-5 h-5 mr-2" />
            Créer un rôle
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sous-rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé le
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {role.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {role.sub_role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(role.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenDialog(role)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(role)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Dialog */}
        {openDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedRole ? 'Modifier le rôle' : 'Créer un rôle'}
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                        Rôle
                      </label>
                      <input
                        type="text"
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="sub_role" className="block text-sm font-medium text-gray-700 mb-1">
                        Sous-rôle
                      </label>
                      <input
                        type="text"
                        id="sub_role"
                        name="sub_role"
                        value={formData.sub_role}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                  <button
                    type="button"
                    onClick={handleCloseDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Enregistrement...' : selectedRole ? 'Enregistrer' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {openDeleteDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Supprimer le rôle
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer ce rôle ?
                </p>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDeleteDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {saving ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {formError && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {formError}
          </div>
        )}

        {formSuccess && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            Opération réussie
          </div>
        )}
            </Tab.Panel>

            {/* Tab 2: Role Translations */}
            <Tab.Panel>
              <div className="mb-4 flex justify-between items-center">
                <div className="flex space-x-4">
                  <input
                    type="text"
                    placeholder="Filtrer par clé..."
                    value={filterKey}
                    onChange={(e) => setFilterKey(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <select
                    value={filterLanguage}
                    onChange={(e) => setFilterLanguage(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Toutes les langues</option>
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{languageNames[lang]}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => handleOpenTranslationDialog()}
                  className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
                >
                  <AddIcon className="w-5 h-5 mr-2" />
                  Créer une traduction
                </button>
              </div>

              {translationsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <CircularProgress />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clé
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Langue
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Label
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {translations.map((translation) => (
                        <tr key={translation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {translation.key}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {languageNames[translation.language] || translation.language}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {translation.label}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleOpenTranslationDialog(translation)}
                              className="text-blue-600 hover:text-blue-900 mr-2"
                              title="Modifier"
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenTranslateDialog(translation)}
                              className="text-purple-600 hover:text-purple-900 mr-2"
                              title="Traduire dans les autres langues"
                            >
                              <TranslateIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteTranslationDialog(translation)}
                              className="text-red-600 hover:text-red-900"
                              title="Supprimer"
                            >
                              <DeleteIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {translations.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      Aucune traduction trouvée
                    </div>
                  )}
                </div>
              )}

              {/* Translation Create/Edit Dialog */}
              {openTranslationDialog && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
                  <div className="bg-white rounded-lg max-w-md w-full">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">
                        {selectedTranslation ? 'Modifier la traduction' : 'Créer une traduction'}
                      </h3>
                    </div>

                    <form onSubmit={handleTranslationSubmit}>
                      <div className="px-6 py-4">
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="translation-key" className="block text-sm font-medium text-gray-700 mb-1">
                              Clé
                            </label>
                            <input
                              type="text"
                              id="translation-key"
                              name="key"
                              value={translationFormData.key}
                              onChange={handleTranslationInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              required
                              placeholder="role-dentist"
                            />
                          </div>

                          <div>
                            <label htmlFor="translation-language" className="block text-sm font-medium text-gray-700 mb-1">
                              Langue
                            </label>
                            <select
                              id="translation-language"
                              name="language"
                              value={translationFormData.language}
                              onChange={handleTranslationInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              required
                            >
                              {languages.map(lang => (
                                <option key={lang} value={lang}>{languageNames[lang]}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor="translation-label" className="block text-sm font-medium text-gray-700 mb-1">
                              Label
                            </label>
                            <input
                              type="text"
                              id="translation-label"
                              name="label"
                              value={translationFormData.label}
                              onChange={handleTranslationInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                        <button
                          type="button"
                          onClick={handleCloseTranslationDialog}
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                            ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                            focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                        >
                          {saving ? 'Enregistrement...' : selectedTranslation ? 'Enregistrer' : 'Créer'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Translation Delete Confirmation Dialog */}
              {openDeleteTranslationDialog && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
                  <div className="bg-white rounded-lg max-w-md w-full">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">
                        Supprimer la traduction
                      </h3>
                    </div>

                    <div className="px-6 py-4">
                      <p className="text-sm text-gray-500">
                        Êtes-vous sûr de vouloir supprimer cette traduction ?
                      </p>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                      <button
                        type="button"
                        onClick={handleCloseDeleteTranslationDialog}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteTranslation}
                        disabled={saving}
                        className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        {saving ? 'Suppression...' : 'Supprimer'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {translationFormError && (
                <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
                  {translationFormError}
                </div>
              )}

              {/* Translation Dialog */}
              {openTranslateDialog && selectedTranslation && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
                  <div className="bg-white rounded-lg max-w-md w-full">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">
                        Traduire la clé
                      </h3>
                    </div>

                    <div className="px-6 py-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Cette action va traduire la clé <strong>{selectedTranslation.key}</strong> depuis {languageNames[selectedTranslation.language]} vers les autres langues supportées.
                      </p>
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Traductions à effectuer :</p>
                        {SUPPORTED_LANGUAGES.filter(lang => lang !== selectedTranslation.language).map(lang => (
                          <div key={lang} className="text-sm text-gray-600 mb-1">
                            • {languageNames[selectedTranslation.language]} → {languageNames[lang]}
                          </div>
                        ))}
                      </div>
                      {translating && (
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" 
                              style={{ width: '100%' }}
                            ></div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            Traduction en cours...
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                      <button
                        type="button"
                        onClick={handleCloseTranslateDialog}
                        disabled={translating}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleTranslate}
                        disabled={translating}
                        className="inline-flex justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        {translating ? (
                          <>
                            <CircularProgress size={20} className="mr-2 text-white" />
                            Traduction en cours...
                          </>
                        ) : (
                          'Lancer la traduction'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
} 